import { useCallback, useRef, useState } from "react";
import { fileToDataUrl } from "../utils/cropImage";

interface CropSession {
  src: string;
  fileName: string;
  mimeType: string;
  queue: File[];
  onDone: (files: File[]) => void;
}

export function useImageCrop() {
  const [session, setSession] = useState<CropSession | null>(null);
  const resultRef = useRef<File[]>([]);

  const openCrop = useCallback(
    async (files: File[], onDone: (croppedFiles: File[]) => void) => {
      if (!files.length) return;
      resultRef.current = [];
      const [first, ...rest] = files;
      const src = await fileToDataUrl(first);
      setSession({ src, fileName: first.name.replace(/\.[^/.]+$/, ".jpg"), mimeType: "image/jpeg", queue: rest, onDone });
    },
    [],
  );

  const handleCropDone = useCallback(
    async (croppedFile: File) => {
      if (!session) return;
      resultRef.current = [...resultRef.current, croppedFile];

      if (session.queue.length === 0) {
        session.onDone(resultRef.current);
        setSession(null);
        return;
      }

      const [next, ...rest] = session.queue;
      const src = await fileToDataUrl(next);
      setSession({ ...session, src, fileName: next.name.replace(/\.[^/.]+$/, ".jpg"), mimeType: "image/jpeg", queue: rest });
    },
    [session],
  );

  const handleCropCancel = useCallback(() => {
    if (!session) return;
    if (resultRef.current.length > 0) {
      session.onDone(resultRef.current);
    }
    setSession(null);
  }, [session]);

  return { session, openCrop, handleCropDone, handleCropCancel };
}
